import { computed, ref, watch } from 'vue';

import { useSubmitTravelerRating } from '../api/useSubmitTravelerRating';
import { useTravelerOrders } from '../api/useTravelerOrders';
import { isPopupViewedForOrder, markPopupViewedForOrders } from '../lib/popupViewedCookie';
import { buildReviewUrlWithRate, extractReviewHash } from '../lib/reviewHash';
import { sortPendingReviewsByFreshness } from '../lib/sortPendingReviews';
import { toEligiblePendingReviewOrder } from '../lib/timeWindow';

import type { PendingReviewOrder } from '../model/types';

export const usePendingReviews = () => {
  const shouldAutoOpenPopup = ref(true);
  const isPopupOpen = ref(false);

  const { orders: rawOrders, isAuthorized } = useTravelerOrders();
  const { submitRating: submitRatingMutation, isSubmitting: isSubmittingRating } = useSubmitTravelerRating();

  const pendingOrders = computed<PendingReviewOrder[]>(() => {
    if (!rawOrders.value?.length) {
      return [];
    }

    const now = new Date();

    const eligible = rawOrders.value.flatMap((order) => {
      const pending = toEligiblePendingReviewOrder(order, now);

      return pending ? [pending] : [];
    });

    return sortPendingReviewsByFreshness(eligible);
  });

  const popupOrder = computed(() => pendingOrders.value.find((order) => !isPopupViewedForOrder(order.id)) ?? null);

  watch(
    [popupOrder, rawOrders],
    ([candidate]) => {
      if (!candidate || !shouldAutoOpenPopup.value || !rawOrders.value) {
        return;
      }

      isPopupOpen.value = true;
      // Помечаем просмотренными ВСЕ ожидающие оценки заказы, а не только показанный:
      // после закрытия/оценки и обновления страницы модалка больше не всплывает,
      // и вернётся только при появлении новых непросмотренных заказов.
      markPopupViewedForOrders(pendingOrders.value.map((order) => order.id));
      shouldAutoOpenPopup.value = false;
    },
    { immediate: true },
  );

  const closePopup = () => {
    isPopupOpen.value = false;
  };

  const submitRating = async (order: PendingReviewOrder, rate: number) => {
    const reviewUrl = order.review_url;
    const reviewHash = extractReviewHash(reviewUrl);

    if (!reviewHash || !reviewUrl || isSubmittingRating.value) {
      return;
    }

    // Открываем страницу отзыва синхронно — в ответ на клик пользователя.
    // Если сделать это после await мутации, браузер заблокирует новую вкладку.
    window.open(buildReviewUrlWithRate(reviewUrl, rate), '_blank', 'noopener,noreferrer');

    try {
      await submitRatingMutation({ reviewHash, rate });
      closePopup();
    } catch (submitError) {
      console.error('Failed to submit traveler rating', submitError);
    }
  };

  return {
    isAuthorized,
    popupOrder,
    isPopupOpen,
    isSubmittingRating: computed(() => isSubmittingRating.value),
    closePopup,
    submitRating,
  };
};
